import { forwardRef, memo, useState, useRef, useCallback, useMemo } from 'react';
import { SearchIcon } from '@page/icons';

import { useMergedRefs } from '@page/core/hooks/common';
import { Popover, PopoverTrigger } from '../../designSystem';
import { Collapsible } from '../Collapsible';
import { TagInput } from '../TagInput';
import { cn } from '../../lib';
import { SearchInputProps, SearchInputOpenChangeReason } from './SearchInput.types';
import { SearchInputContext } from './SearchInput.context';
import { SearchInputPopover } from './SearchInputPopover';

const SearchInputBase = (
  {
    id,
    className,
    type = 'text',
    tags = [],
    onTagsChange,
    onSearch,
    placeholder = 'Search',
    disabled,
    collapsible = true,
    collapsedClassName,
    expandedClassName,
    alwaysExpanded = false,
    renderSuggestions,
    popoverContent,
    open: openProp,
    inputValue: inputValueProp,
    onInputChange,
    onOpenChange,
    onFocus,
    onBlur,
    onClear,
    popoverClassName,
    expandedPlaceholder,
    popoverAlign,
    showOverflowTagsMode = 'none',
    hideLeftIcon = false,
    ...rest
  }: SearchInputProps,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
) => {
  const actualId = id || rest.name;
  const [_inputValue, _setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const internalRef = useRef<HTMLInputElement | null>(null);
  const ref = useMergedRefs<HTMLInputElement>(forwardedRef, internalRef);

  const inputValue = inputValueProp ?? _inputValue;

  const setInputValue = useCallback(
    (value: string) => {
      _setInputValue(value);
      onInputChange?.(value);
    },
    [onInputChange],
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean, reason?: SearchInputOpenChangeReason) => {
      setOpen(isOpen);
      onOpenChange?.(isOpen, reason);
    },
    [onOpenChange],
  );

  const handleAddTag = useCallback(
    (tag: string) => {
      const trimmedTag = tag.trim();
      if (trimmedTag && !tags.includes(trimmedTag)) {
        const newTags = [...tags, trimmedTag];
        setInputValue('');
        onTagsChange?.(newTags, trimmedTag);

        onSearch?.(newTags);
        internalRef.current?.focus();
      }
    },
    [tags, setInputValue, onTagsChange, onSearch],
  );

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      const newTags = tags.filter((tag) => tag !== tagToRemove);
      onTagsChange?.(newTags, tagToRemove);
      internalRef.current?.focus();
    },
    [tags, onTagsChange],
  );

  const handleClear = () => {
    internalRef.current?.blur();
    handleOpenChange(false, 'clear');
    onClear?.();
  };

  const forceCloseDropdown = !inputValue && tags.length === 0 && !renderSuggestions;

  const finalOpen = openProp ?? (open && !forceCloseDropdown);

  const contextValue = useMemo(
    () => ({
      handleAddTag,
      handleRemoveTag,
    }),
    [handleAddTag, handleRemoveTag],
  );

  const handleTagClick = useCallback(() => {
    internalRef.current?.focus();
  }, []);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      handleOpenChange(true, 'focus');
      return onFocus?.(e);
    },
    [handleOpenChange, onFocus],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      return onBlur?.(e);
    },
    [onBlur],
  );

  const handleOverflowCountTagClick = useCallback(
    (currentPopoverOpen: boolean) => {
      if (showOverflowTagsMode === 'popover') {
        handleOpenChange(false, 'tag-overflow-click');
        if (currentPopoverOpen) {
          internalRef.current?.focus();
        }
      }
    },
    [handleOpenChange, showOverflowTagsMode],
  );

  return (
    <SearchInputContext.Provider value={contextValue}>
      <Popover open={finalOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Collapsible
            alwaysExpanded={!collapsible || tags.length > 0 || alwaysExpanded || finalOpen}
            collapsedClassName={cn('w-23 min-w-23', className, collapsedClassName)}
            expandedClassName={cn('min-w-80', className, expandedClassName)}
          >
            {({ isExpanded }) => (
              <TagInput
                inputContainerClassName={cn('min-h-8 items-center flex-nowrap py-0', {
                  'focus-within:border-bg-inter-secondary-default': !isExpanded,
                })}
                inputClassName={cn('py-0 h-6', {
                  'placeholder:text-text-primary': !isFocused && !isExpanded,
                })}
                overflow="slice"
                ref={ref}
                id={actualId}
                type={type}
                tags={tags}
                onTagsChange={onTagsChange}
                tagClassName="py-0 pl-2 pr-1.5 cursor-pointer"
                onTagClick={handleTagClick}
                onOverflowCountTagClick={handleOverflowCountTagClick}
                placeholder={isExpanded ? expandedPlaceholder || placeholder : placeholder}
                disabled={disabled}
                variant="default"
                inputValue={inputValue}
                onInputChange={setInputValue}
                leftIcon={
                  !hideLeftIcon && (
                    <SearchIcon className={cn('size-5 transition-colors fill-icon-primary pointer-events-none')} />
                  )
                }
                clearable={alwaysExpanded ? !!tags.length : true}
                forceShowClearButton={isExpanded}
                onClear={handleClear}
                onFocus={handleFocus}
                onBlur={handleBlur}
                addTagOnBlur={false}
                showOverflowTagsMode={showOverflowTagsMode}
                {...rest}
              />
            )}
          </Collapsible>
        </PopoverTrigger>

        <SearchInputPopover
          inputValue={inputValue}
          tags={tags}
          renderSuggestions={renderSuggestions}
          content={popoverContent}
          className={popoverClassName}
          align={popoverAlign}
        />
      </Popover>
    </SearchInputContext.Provider>
  );
};

export const SearchInput = memo(forwardRef(SearchInputBase));
